import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { ResendSettings } from "./ResendSettings";

const labelCls = "text-[11px] font-bold uppercase tracking-wider text-slate-500";

export const EmailSettings = () => {
  const { t } = useLang();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/email").then(({ data }) => setForm(data)).catch((e) => toast.error(errorMessage(e))); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { enabled: form.enabled, from_name: form.from_name, reply_to: form.reply_to };
    try {
      const { data } = await api.put("/admin/settings/email", body);
      setForm(data);
      toast.success(t("Email settings saved"));
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  if (!form) return <div className="h-24 animate-pulse rounded-2xl bg-slate-200" />;
  return (
    <form onSubmit={save} className="space-y-3 rounded-2xl border border-slate-200/80 bg-white p-4" data-testid="email-settings">
      <div className="flex flex-wrap items-center gap-3">
        <Mail className="h-4 w-4 text-slate-500" />
        <h3 className="me-auto font-display text-base font-bold text-slate-900">{t("Email settings")}</h3>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Switch checked={form.enabled} onCheckedChange={(enabled) => setForm({ ...form, enabled })} data-testid="email-enabled-switch" />
          {t(form.enabled ? "Notification emails on" : "Notification emails off")}
        </label>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div className="space-y-1">
          <label className={labelCls} htmlFor="email-from-name">{t("Sender name")}</label>
          <Input id="email-from-name" required maxLength={60} value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })}
            className="h-10 rounded-xl" data-testid="email-from-name-input" />
        </div>
        <div className="space-y-1">
          <label className={labelCls} htmlFor="email-reply-to">{t("Reply-to address")}</label>
          <Input id="email-reply-to" type="email" dir="ltr" value={form.reply_to} placeholder="hr@inception.sa"
            onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="h-10 rounded-xl" data-testid="email-reply-to-input" />
        </div>
        <button type="submit" disabled={saving} data-testid="email-settings-save"
          className="btn-press min-h-[40px] rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white disabled:opacity-60">
          {saving ? t("Saving…") : t("Save")}
        </button>
      </div>
      <p className="text-xs text-slate-400">{t("Sign-in codes are always sent, even when notification emails are off. Replies go to the reply-to address.")}</p>
      <ResendSettings settings={form} onSaved={setForm} />
    </form>
  );
};
